import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";

import API_BASE_URL from "config/api";
import FAQ from "./FAQ";

/**
 * FaqFromApi (wrapper)
 *
 * Two-layer structure:
 * - `components/FAQ` is presentational (only receives `items`)
 * - This wrapper fetches items from backend and passes them down
 *
 * API (expected): GET `${API_BASE_URL}/web2/faq/?tag=ios,android&lang=zh`
 */
function FaqFromApi({ tag, tags, lang, titleKey, defaultExpanded }) {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);

  const tagList = useMemo(() => {
    // Support both `tag` (string) and `tags` (array)
    const list = [];

    if (typeof tag === "string" && tag.trim()) {
      list.push(tag.trim());
    }

    if (Array.isArray(tags)) {
      tags.filter((t) => typeof t === "string" && t.trim()).forEach((t) => list.push(t.trim()));
    }

    // unique + stable
    return Array.from(new Set(list));
  }, [tag, tags]);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();

    if (tagList.length > 0) {
      params.set("tag", tagList.join(","));
    }

    if (typeof lang === "string" && lang.trim()) {
      params.set("lang", lang.trim());
    }

    const qs = params.toString();
    return qs ? `?${qs}` : "";
  }, [tagList, lang]);

  useEffect(() => {
    let cancelled = false;

    const fetchFaq = async () => {
      setError(null);

      try {
        const url = `${API_BASE_URL}/web2/faq/${queryString}`;
        const res = await axios.get(url);

        // Expected response shape: { items: [{ question, answer, updated_at, ... }], ... }
        // Also tolerate: { results: [...] } or direct array.
        const raw =
          (res && res.data && (res.data.items || res.data.results)) ||
          (Array.isArray(res.data) ? res.data : []);

        const mapped = Array.isArray(raw)
          ? raw
              .filter(Boolean)
              .map((row, idx) => {
                // Backend might return either localized fields already (question/answer)
                // or raw model fields (question_zh/answer_zh, question_en/answer_en)
                const q =
                  row.question ||
                  row.q ||
                  row.question_text ||
                  row.question_zh ||
                  row.question_en ||
                  "";
                const a =
                  row.answer || row.a || row.answer_text || row.answer_zh || row.answer_en || "";

                // stable key preference order
                const key =
                  row.id != null
                    ? `faq-${row.id}`
                    : row.pk != null
                    ? `faq-${row.pk}`
                    : row.key
                    ? String(row.key)
                    : `faq-${idx}`;

                return { key, q, a };
              })
              .filter((it) => it.q || it.a)
          : [];

        if (!cancelled) {
          setItems(mapped);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e);
          setItems([]);
        }
      }
    };

    fetchFaq();

    return () => {
      cancelled = true;
    };
  }, [queryString]);

  // Wrapper always renders to keep the title stable
  if (error) {
    // eslint-disable-next-line no-console
    console.error("FaqFromApi: failed to load FAQ", error);
  }

  return <FAQ items={items} titleKey={titleKey} defaultExpanded={defaultExpanded} />;
}

FaqFromApi.propTypes = {
  // Single tag
  tag: PropTypes.string,
  // Multiple tags
  tags: PropTypes.arrayOf(PropTypes.string),
  // Optional language hint (backend may ignore if it detects from headers)
  lang: PropTypes.string,
  // i18n title key passed to FAQ
  titleKey: PropTypes.string,
  // Expand all by default
  defaultExpanded: PropTypes.bool,
};

FaqFromApi.defaultProps = {
  tag: "",
  tags: [],
  lang: "",
  titleKey: "faq.title",
  defaultExpanded: false,
};

export default FaqFromApi;
